<template>
  <div class="camp-archive">
    <!-- 学员档案头 -->
    <div class="archive-head">
      <div class="archive-profile">
        <img class="profile-avatar" src="@/assets/images/researchCamp/avatar.png" alt="">
        <div>
          <div class="profile-name">{{ student.name }}</div>
          <div class="profile-meta" v-if="student.campName">{{ student.campName }}</div>
          <div class="profile-meta">{{ student.hospital }} {{ student.department }} {{ student.title }}</div>
          <div class="profile-meta">入营时间：{{ student.joinDate }}</div>
        </div>
      </div>
    </div>
    <!-- 阶段进度 -->
    <camp-level-progress class="archive-progress" :levels="archive && archive.levels ? archive.levels : []" :member="archive && archive.member ? archive.member : {}" empty-text="暂无等级进度"></camp-level-progress>
    <!-- 任务完成进度 -->
    <div class="archive-rate">
      <div class="rate-title">任务完成进度 <span>入营至今</span></div>
      <div class="rate-grid">
        <div class="rate-item" v-for="item in rates" :key="item.label" @click="item.clickable && openRate(item)">
          <div class="rate-label">{{ item.label }}</div>
          <div class="rate-value">{{ item.value }}<span v-if="item.suffix">{{ item.suffix }}</span><i class="value-arrow" v-if="item.arrow"></i></div>
          <div class="rate-extra" v-if="item.extraTail">平均：{{ item.extraTail }}</div>
        </div>
      </div>
      <div class="rate-grid rate-grid-sub">
        <div class="rate-item" v-for="item in practices" :key="item.label" @click="item.clickable && openRate(item)">
          <div class="rate-label">{{ item.label }}</div>
          <div class="rate-value">{{ item.value }}<span v-if="item.suffix">{{ item.suffix }}</span><i class="value-arrow" v-if="item.arrow"></i></div>
        </div>
      </div>
    </div>
    <!-- 所属中心 -->
    <camp-center-entry :camp="camp" @open="goDetail"></camp-center-entry>
    <!-- 进度明细 -->
    <van-popup v-model="popupVisible" class="archive-popup" get-container="body" :overlay-style="{ background: 'rgba(0, 0, 0, 0.5)' }">
      <div class="popup-title">{{ popupTitle }}</div>
      <div class="popup-sub" v-if="popupSub">{{ popupSub }}</div>
      <div class="popup-state" v-if="!popupLoading && !popupError && !popupRows.length">暂无明细数据</div>
      <!-- 课程任务列表 -->
      <div class="popup-list" v-if="!popupLoading && !popupError && popupRows.length && popupType === 'course'">
        <div class="course-row" v-for="item in popupRows" :key="item.id">
          <div class="course-top">
            <div class="course-name">
              {{ item.name }}
              <span class="course-tag" v-if="item.custom">定制</span>
            </div>
            <div class="course-status" :class="item.tone"><i></i>{{ item.value }}</div>
          </div>
          <div class="course-line">
            <span>{{ item.hospital }}</span>
            <span class="course-date">{{ item.date }}</span>
          </div>
          <div class="course-teacher" v-if="item.teacher">{{ item.teacher }}</div>
        </div>
      </div>
      <!-- 打卡任务列表 -->
      <div class="popup-list" v-else-if="!popupLoading && !popupError && popupRows.length && popupType === 'checkin'">
        <div class="check-row" v-for="item in popupRows" :key="item.id">
          <div class="check-top">
            <div class="check-name">{{ item.name }}</div>
            <div class="course-status" :class="item.tone"><i></i>{{ item.value }}</div>
          </div>
          <div class="check-date">{{ item.date }}</div>
          <div class="check-date" v-if="item.description">{{ item.description }}</div>
        </div>
      </div>
      <!-- 考试、模拟器状态列表 -->
      <div class="popup-list" v-else-if="!popupLoading && !popupError && popupRows.length && popupType === 'status'">
        <div class="status-row" v-for="item in popupRows" :key="item.id">
          <div>
            <div class="status-name">{{ item.name }}</div>
            <div class="popup-meta" v-if="item.meta">{{ item.meta }}</div>
          </div>
          <div class="course-status" :class="item.tone"><i></i>{{ item.value }}</div>
        </div>
      </div>
      <!-- 其他明细 -->
      <div class="popup-list" v-else-if="!popupLoading && !popupError && popupRows.length">
        <div class="popup-row" v-for="item in popupRows" :key="item.id">
          <div>
            <div class="popup-name">{{ item.name }}</div>
            <div class="popup-meta" v-if="item.meta">{{ item.meta }}</div>
          </div>
          <span :class="item.tone">{{ item.value }}</span>
        </div>
      </div>
      <button class="popup-btn" type="button" @click="popupVisible = false">关闭</button>
    </van-popup>
  </div>
</template>

<script>
import withRouteTracking from '@/utils/withRouteTracking'
import CampLevelProgress from './components/CampLevelProgress.vue'
import CampCenterEntry from './components/CampCenterEntry.vue'
import { formatCampDate } from './date'
import {
  getResearchCampMemberArchive,
  getResearchCampMemberCheckins,
  getResearchCampMemberTasks
} from '@/api/researchCamp'

const CampArchive = {
  name: 'CampArchive',
  components: {
    CampLevelProgress,
    CampCenterEntry
  },
  data () {
    return {
      archive: null,
      archiveRequestSeq: 0,
      popupVisible: false,
      popupLoading: false,
      popupError: '',
      popupType: '',
      popupTitle: '',
      popupSub: '',
      popupRows: []
    }
  },
  computed: {
    // 学员基本资料
    student () {
      const member = this.archive && this.archive.member ? this.archive.member : {}
      const profile = this.archive && this.archive.iepProfile ? this.archive.iepProfile : {}
      const camp = this.archive && this.archive.camp ? this.archive.camp : {}
      return {
        name: profile.name || member.name || '',
        campName: camp.campName || member.campName || '',
        hospital: profile.hospitalName || member.hospitalName || '',
        department: profile.departmentL1 || member.departmentL1 || '',
        title: profile.positionTitle || member.positionTitle || '',
        joinDate: this.formatDate(member.entryTime)
      }
    },
    // 所属研修营
    camp () {
      return this.archive && this.archive.camp ? this.archive.camp : {}
    },
    // 完成率按类型索引
    statisticMap () {
      const rows = this.archive && this.archive.completionStatistics
        ? this.archive.completionStatistics
        : []
      return rows.reduce((map, item) => {
        map[item.key] = item
        return map
      }, {})
    },
    // 课程、考试、打卡完成率
    rates () {
      const course = this.statisticMap.course || {}
      const customCourse = this.statisticMap.customCourse || {}
      const exam = this.statisticMap.exam || {}
      const customExam = this.statisticMap.customExam || {}
      const checkin = this.statisticMap.checkin || {}
      return [
        Object.assign(this.rateItem('课程', course, 'course'), { statisticKey: 'course', sourceFilter: 'FIXED' }),
        Object.assign(this.rateItem('定制课程', customCourse, 'course'), { statisticKey: 'customCourse', sourceFilter: 'CUSTOM' }),
        Object.assign(this.rateItem('考试', exam, 'exam'), { statisticKey: 'exam', sourceFilter: 'FIXED' }),
        Object.assign(this.rateItem('定制考试', customExam, 'exam'), { statisticKey: 'customExam', sourceFilter: 'CUSTOM' }),
        Object.assign(this.rateItem('打卡', checkin, 'checkin'), { statisticKey: 'checkin' })
      ]
    },
    // 模拟器和实操完成情况
    practices () {
      return [
        Object.assign(this.rateItem('虚拟模拟器', this.statisticMap.virtualSimulator, 'simulator'), {
          statisticKey: 'virtualSimulator'
        }),
        this.rateItem('实体模拟器', this.statisticMap.physicalSimulator, 'physicalSimulator'),
        this.rateItem('病例复盘', this.statisticMap.caseReview, 'caseReview'),
        this.rateItem('上手实操', this.statisticMap.handsOnPractice, 'practice')
      ]
    },
  },
  created () {
    this.loadArchive()
  },
  watch: {
    '$route.query.studentId' () {
      this.loadArchive()
    },
    '$route.query.viewRole' () {
      this.loadArchive()
    }
  },
  methods: {
    // 新接口按三态展示，兼容旧接口仅返回 done 的响应。
    taskState (item, doneText) {
      const status = item && item.status ? item.status : (item && item.done ? 'DONE' : 'TODO')
      if (status === 'DONE') return { tone: 'done', text: doneText }
      if (status === 'IN_PROGRESS') return { tone: 'doing', text: '进行中' }
      return { tone: 'pending', text: '未开始' }
    },
    // 加载学员档案
    async loadArchive () {
      const requestSeq = ++this.archiveRequestSeq
      const campMemberId = this.$route.query.studentId
      const viewRole = this.$route.query.viewRole
      if (!campMemberId) {
        this.$toast('缺少学员信息')
        return
      }
      if (!viewRole) {
        this.archive = null
        this.$toast('缺少进入档案时选择的角色')
        return
      }
      try {
        const response = await getResearchCampMemberArchive(campMemberId, { viewRole })
        if (requestSeq !== this.archiveRequestSeq) return
        this.archive = response && response.data ? response.data : null
        if (!this.archive || !this.archive.member) this.$toast('未获取到学员档案')
      } catch (error) {
        if (requestSeq !== this.archiveRequestSeq) return
        this.$toast(error && error.msg ? error.msg : '学员档案加载失败')
      }
    },
    // 完成率格子的展示数据
    rateItem (label, statistic, key) {
      const row = statistic || {}
      const averageScore = key === 'exam' ? this.formatAverageScore(row.averageScore) : ''
      return {
        label,
        value: String(row.done === null || row.done === undefined ? 0 : row.done),
        suffix: row.unit || (row.total === null || row.total === undefined ? '' : `/${row.total}`),
        extraTail: averageScore,
        arrow: ['course', 'exam', 'checkin', 'simulator'].indexOf(key) >= 0,
        clickable: ['course', 'exam', 'checkin', 'simulator'].indexOf(key) >= 0,
        key,
        statisticKey: key
      }
    },
    // 平均分最多保留两位小数，并移除无意义的末尾零。
    formatAverageScore (value) {
      if (value === null || value === undefined || value === '') return ''
      const score = Number(value)
      return Number.isFinite(score) ? score.toFixed(2).replace(/\.?0+$/, '') : ''
    },
    // 日期格式化。数组走统一方法
    formatDate (value) {
      if (!value) return ''
      if (Array.isArray(value)) return formatCampDate(value)
      const raw = String(value)
      if (/^\d+$/.test(raw)) {
        const date = new Date(Number(raw))
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      }
      return raw.slice(0, 10)
    },
    // 打开完成率明细。课程和考试进任务列表
    async openRate (item) {
      this.popupType = 'status'
      this.popupTitle = item.label
      this.popupSub = ''
      this.popupRows = []
      this.popupError = ''
      this.popupLoading = false
      this.popupVisible = true
      const statistic = this.statisticMap[item.statisticKey || item.key]

      if (item.key === 'course' || item.key === 'exam') {
        this.popupType = item.key === 'course' ? 'course' : 'status'
        this.popupTitle = item.key === 'course' ? '课程任务列表' : '考试任务列表'
        await this.loadTaskRows(item.key === 'course' ? 'COURSE' : 'EXAM', statistic, item.sourceFilter)
        return
      }
      if (item.key === 'checkin') {
        this.popupType = 'checkin'
        this.popupTitle = '打卡任务列表'
        await this.loadCheckinRows(statistic)
        return
      }
      if (item.key === 'simulator') {
        const details = statistic && statistic.details ? statistic.details : []
        this.popupSub = `已通过 ${statistic ? statistic.done : 0}/${statistic && statistic.total !== null ? statistic.total : 4}`
        this.popupRows = details.map(row => ({
          id: row.key,
          name: row.label,
          value: row.passed ? '已通过' : '未通过',
          tone: row.passed ? 'pass' : 'pending'
        }))
        return
      }
      this.popupSub = '该项仅提供汇总数据'
    },
    // 加载课程或考试任务明细
    async loadTaskRows (type, statistic, sourceFilter) {
      const campMemberId = this.$route.query.studentId
      this.popupLoading = true
      try {
        const response = await getResearchCampMemberTasks(campMemberId, {
          type,
          source: sourceFilter,
          viewRole: this.$route.query.viewRole
        })
        const rows = response && Array.isArray(response.data) ? response.data : []
        const filtered = sourceFilter ? rows.filter(row => row.source === sourceFilter) : rows
        this.popupSub = `已完成 ${statistic ? statistic.done : filtered.filter(row => row.done).length}/${statistic && statistic.total !== null ? statistic.total : filtered.length}`
        this.popupRows = filtered.map(row => {
          const state = this.taskState(row, type === 'EXAM' ? '已提交' : '已完成')
          return {
            id: `${row.source}-${row.id}-${row.dispatchMemberId || ''}`,
            name: row.name || '',
            hospital: row.courseHospitalName ? `医院：${row.courseHospitalName}` : '',
            teacher: row.courseLecturerNames ? `讲师：${row.courseLecturerNames}` : '',
            date: this.formatDate(row.latestSubmittedAt),
            meta: row.latestScore === null || row.latestScore === undefined ? '' : `最近提交：${row.latestScore}分`,
            value: state.text,
            tone: state.tone,
            custom: row.source === 'CUSTOM'
          }
        })
      } catch (error) {
        this.popupError = error && error.msg ? error.msg : '任务明细加载失败'
        this.$toast(this.popupError)
      } finally {
        this.popupLoading = false
      }
    },
    // 加载打卡明细
    async loadCheckinRows (statistic) {
      this.popupLoading = true
      try {
        const response = await getResearchCampMemberCheckins(this.$route.query.studentId, {
          viewRole: this.$route.query.viewRole
        })
        const rows = response && response.data ? response.data : []
        this.popupSub = `已完成 ${statistic ? statistic.done : rows.filter(row => row.completed).length}/${statistic && statistic.total !== null ? statistic.total : rows.length}`
        this.popupRows = rows.map(row => ({
          id: row.planItemId,
          name: [row.taskType, row.name].filter(Boolean).join('：'),
          date: row.checkinDate ? `打卡日期：${this.formatDate(row.checkinDate)}` : '',
          description: row.description || '',
          value: row.completed ? '已完成' : row.unlocked ? '未打卡' : '未解锁',
          tone: row.completed ? 'done' : row.unlocked ? 'pending' : 'pending'
        }))
      } catch (error) {
        this.popupError = error && error.msg ? error.msg : '打卡明细加载失败'
        this.$toast(this.popupError)
      } finally {
        this.popupLoading = false
      }
    },
    // 进入研修营详情
    goDetail () {
      const researchCampId = this.camp.campConfigId || this.$route.query.campConfigId
      if (!researchCampId) return
      this.$router.push({ path: '/researchCamp/detail', query: { researchCampId } })
    }
  }
}

export default withRouteTracking(CampArchive)
</script>

<style lang="less" scoped>
@import './camp.less';

.camp-archive {
  .camp-page();
  padding-bottom: 24px;
  background-image: url('../../assets/images/researchCamp/banner.png');
  background-repeat: no-repeat;
  background-position: center top;
  background-size: 100% auto;

  .archive-state {
    padding: 24px 14px;
    text-align: center;
    font-size: 12px;
    color: @camp-sub;
  }

  .archive-head {
    padding-top: 170px;
  }

  .archive-profile {
    .camp-white-card();
    display: flex;
    padding: 16px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

    .profile-avatar {
      width: 42px;
      height: 42px;
      margin-right: 12px;
      border-radius: 50%;
      object-fit: cover;
      overflow: hidden;
      flex-shrink: 0;
    }

    .profile-name {
      font-size: 14px;
      line-height: 20px;
      margin-top: 4px;
    }

    .profile-meta {
      font-size: 12px;
      line-height: 20px;
      color: @camp-sub;
      word-break: break-all;
    }
  }

  .archive-progress {
    margin-top: 13px;
  }

  .archive-rate {
    .camp-white-card();
    margin-top: 13px;
    padding: 16px 10px 10px 10px;
    background: #eb1700 url('../../assets/images/researchCamp/ph_bg.png') no-repeat center / cover;
  }

  .rate-title {
    margin-bottom: 14px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;

    span {
      margin-left: 6px;
      font-size: 12px;
      font-weight: 400;
    }
  }

  .rate-grid {
    display: flex;
    gap: 8px;

    .rate-item {
      flex: 1;
      min-width: 0;
      padding: 8px 0;
      border-radius: 6px;
      text-align: center;
      background: #fff;
    }

    .rate-label {
      font-size: 10px;
      color: #564C47;
    }

    .rate-value,
    .rate-extra {
      color: #000;
    }

    .rate-value {
      font-size: 16px;
      font-weight: 500;

      span {
        font-size: 10px;
        font-weight: 400;
      }
    }

    .value-arrow {
      display: inline-block;
      width: 4px;
      height: 4px;
      margin-left: 2px;
      border-top: 1px solid #564C47;
      border-right: 1px solid #564C47;
      transform: rotate(45deg);
      vertical-align: 1px;
    }

    .rate-extra {
      font-size: 10px;
      font-weight: 400;

      em {
        font-style: normal;
        font-size: 10px;
      }
    }
  }

  .rate-grid-sub {
    margin-top: 10px;
  }

}

.archive-popup {
  display: flex;
  flex-direction: column;
  width: 320px;
  max-height: 540px;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  box-sizing: border-box;

  .popup-title {
    padding-top: 14px;
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    color: rgba(0, 0, 0, 0.8);
  }

  .popup-sub {
    text-align: center;
    font-size: 12px;
    font-weight: 400;
    line-height: 24px;
    color: #81766f;
  }

  .popup-state {
    padding: 24px 12px;
    text-align: center;
    font-size: 12px;
    color: #81766f;
  }

  .popup-list {
    flex: 1;
    overflow-y: auto;
    padding: 4px 16px 0;
  }

  .course-row {
    padding: 12px 0;
    border-bottom: 1px solid #f3f3f3;
  }

  .course-top,
  .course-line {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .course-name,
  .check-name,
  .status-name {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    word-break: break-all;
    flex: 1;
    min-width: 0;
  }

  .course-name {
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    color: #000;
  }

  .course-tag {
    display: inline-block;
    margin-left: 4px;
    padding: 0 6px;
    height: 16px;
    border-radius: 10px;
    background: rgba(235, 23, 0, 0.08);
    color: #eb1700;
    font-size: 11px;
    font-weight: 400;
    line-height: 16px;
    vertical-align: 1px;
  }

  .course-status {
    display: flex;
    align-items: center;
    margin-left: 8px;
    font-size: 11px;
    font-weight: 400;
    line-height: 20px;
    flex-shrink: 0;

    i {
      width: 4px;
      height: 4px;
      margin-right: 4px;
      border-radius: 50%;
      background: currentColor;
    }

    &.doing {
      color: #00a2ff;
    }

    &.done {
      color: #38c976;
    }

    &.pending {
      color: #999;
    }

    &.pass {
      color: #53ce76;
    }
  }

  .status-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #f3f3f3;

    > div:first-child {
      flex: 1;
      min-width: 0;
    }
  }

  .status-name {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #000;
  }

  .course-line,
  .course-teacher {
    font-size: 11px;
    font-weight: 400;
    line-height: 20px;
    color: #81766f;
  }

  .course-date {
    margin-left: 8px;
    flex-shrink: 0;
  }

  .check-row {
    padding: 12px 0;
    border-bottom: 1px solid #f3f3f3;
  }

  .check-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .check-name {
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    color: #000;
  }

  .check-date {
    font-size: 11px;
    font-weight: 400;
    line-height: 20px;
    color: #81766f;
  }

  .popup-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #f3f3f3;
  }

  .popup-name {
    font-size: 14px;
  }

  .popup-meta {
    font-size: 11px;
    color: #81766f;
  }

  .doing {
    color: #00a2ff;
    font-size: 12px;
  }

  .done,
  .pass {
    color: #38c976;
    font-size: 12px;
  }

  .pending {
    color: #999;
    font-size: 12px;
  }

  .popup-btn {
    display: block;
    width: calc(~'100% - 32px');
    height: 40px;
    margin: 12px auto 16px;
    border: 0;
    border-radius: 20px;
    background: #f3f1f0;
    color: #81766f;
    font-size: 14px;
    font-weight: 400;
    flex-shrink: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/archive.vue